import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardContainer, DefaultCard } from '../../../Components/Card/Card';
import VossFeatureFlagsTable from '../../../Components/Voss/FeatureFlags/VossFeatureFlagsTable';
import VossFeatureFlagConfirmModal, { type VossFeatureFlagConfirm } from '../../../Components/Voss/FeatureFlags/VossFeatureFlagConfirmModal';
import type { VossFeatureFlag, VossFeatureFlagUpdate } from '../../../Components/Voss/FeatureFlags/VossFeatureFlags.types';
import vossFeatureFlagActions from '../../../Actions/Voss/vossFeatureFlagActions';
import { useAppDispatch } from '../../../Store/types';
import useModal from '../../../Hooks/useModal';
import Modals from '../../../Constants/modals';

const VossFeatureFlagsContainer = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { isModalOpen, openModal, closeModal } = useModal();
    const [flags, setFlags] = useState<VossFeatureFlag[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [pendingNames, setPendingNames] = useState<ReadonlySet<string>>(() => new Set());
    const [confirm, setConfirm] = useState<VossFeatureFlagConfirm | null>(null);

    const load = useCallback(async () => {
        const result = await dispatch(vossFeatureFlagActions.getAll());
        if (result) setFlags(result);
        return result;
    }, [dispatch]);

    useEffect(() => {
        void load().finally(() => setLoading(false));
    }, [load]);

    const setPending = (name: string, pending: boolean) =>
        setPendingNames((current) => {
            const next = new Set(current);
            if (pending) next.add(name);
            else next.delete(name);
            return next;
        });

    // VOSS needs enabled and description on every PUT. The list on screen may be stale (someone else may have changed the flag),
    // so the field this action does not change is taken from a fresh read, and the list is reloaded afterwards.
    const mutate = async (name: string, action: (fresh: VossFeatureFlag) => Promise<unknown>) => {
        setPending(name, true);
        try {
            const fresh = (await dispatch(vossFeatureFlagActions.getAll()))?.find((f) => f.name === name);
            if (fresh !== undefined) await action(fresh);
        } catch {
            // the error toast has already been dispatched by apiThunk
        } finally {
            await load();
            setPending(name, false);
        }
    };

    const send = (name: string, update: (fresh: VossFeatureFlag) => VossFeatureFlagUpdate) => mutate(name, (fresh) => dispatch(vossFeatureFlagActions.update(name, update(fresh))));

    const askToggle = (name: string, enabled: boolean) => {
        setConfirm({ kind: 'toggle', name, enabled });
        openModal(Modals.ToggleVossFeatureFlag);
    };

    const askDelete = (name: string) => {
        setConfirm({ kind: 'delete', name });
        openModal(Modals.DeleteVossFeatureFlag);
    };

    const saveDescription = (name: string, description: string | null) => {
        void send(name, (fresh) => ({ enabled: fresh.enabled, description }));
    };

    const onConfirmed = () => {
        const pending = confirm;
        closeModal();
        setConfirm(null);
        if (pending === null) return;
        if (pending.kind === 'toggle') {
            // An unsaved description edit in the table is neither sent nor dropped: it stays open and the PUT keeps the server's description.
            void send(pending.name, (fresh) => ({ enabled: pending.enabled, description: fresh.description ?? null }));
        } else {
            void mutate(pending.name, () => dispatch(vossFeatureFlagActions.remove(pending.name)));
        }
    };

    const modalOpen = isModalOpen(Modals.ToggleVossFeatureFlag) || isModalOpen(Modals.DeleteVossFeatureFlag);

    return (
        <CardContainer>
            <DefaultCard title={t('vossfeatureflags.title')} isLoading={loading} headerContent={null} hasNoData={false}>
                {flags && <VossFeatureFlagsTable flags={flags} pendingNames={pendingNames} onToggle={askToggle} onSaveDescription={saveDescription} onDelete={askDelete} />}
            </DefaultCard>
            {modalOpen && confirm !== null && <VossFeatureFlagConfirmModal confirm={confirm} onConfirm={onConfirmed} />}
        </CardContainer>
    );
};

export default VossFeatureFlagsContainer;
